/**
 * dtcService.ts — Spoke-side DTC diagnosis API client.
 *
 * Mirrors diagnosticService.ts. Calls the /api/dtc and /api/work-orders/:woId/dtc
 * routes on the backend.  Uses a dedicated axios instance (baseURL = REACT_APP_API_URL)
 * because the DTC routes live under /api/, NOT /api/admin/ (same as diagnosticService).
 */

import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';
import { logger } from '../utils/logger';

// REACT_APP_API_URL is "<origin>/api". The method paths below already include
// the "/api/" prefix, so the baseURL must be the ORIGIN — otherwise requests
// double up as "<origin>/api/api/..." → 404 (and the error envelope crashes
// the page). Strip a trailing "/api" defensively.
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(
  /\/api\/?$/,
  '',
);
const dtcAxios = axios.create({
  baseURL: API_ORIGIN,
  timeout: 15000,
});

dtcAxios.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

dtcAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          const refreshResp = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`,
            { refreshToken: tokens.refreshToken }
          );
          const { tokens: newTokens } = refreshResp.data;
          saveTokens(newTokens);
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return dtcAxios(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    if (error.response) {
      logger.error('DTC API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
    }
    return Promise.reject(error);
  }
);

// ─── Translation result ──────────────────────────────────────────────────────

export interface DtcTranslateResult {
  code: string;
  system: string;
  definition: string;
  likely_causes: string[];
  related_procedures: string[];
  source: 'cache' | 'hub' | 'ai_estimate';
  attribution: string;
  /** Present when the backend served a degraded / cached result */
  vendor_degraded?: boolean;
  cached?: boolean;
}

// ─── Attached DTC row ────────────────────────────────────────────────────────

export type DtcStatus = 'detected' | 'diagnosing' | 'resolved' | 'dismissed';

export interface AttachedDtc {
  id: string;
  work_order_id: number;
  code: string;
  system: string | null;
  definition: string | null;
  translation_json: DtcTranslateResult;
  source: string;
  status: DtcStatus;
  created_at: string;
  updated_at: string;
}

// ─── C3 diagnosis row ────────────────────────────────────────────────────────

export interface WorkOrderDiagnosis {
  id: string;
  work_order_id: number;
  concern: string | null;
  cause: string | null;
  correction: string | null;
  related_dtc_code: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Request bodies ──────────────────────────────────────────────────────────

export interface DtcTranslateBody {
  code: string;
  vehicleSnapshot?: {
    year?: number;
    make?: string;
    model?: string;
    engine_size?: string;
  };
}

export interface DiagnosisBody {
  concern?: string;
  cause?: string;
  correction?: string;
  related_dtc_code?: string;
}

// ─── API functions ───────────────────────────────────────────────────────────

/**
 * POST /api/dtc/translate
 * Returns a DTC translation result.
 */
async function translate(body: DtcTranslateBody): Promise<DtcTranslateResult> {
  const res = await dtcAxios.post('/api/dtc/translate', body);
  return res.data;
}

/**
 * POST /api/work-orders/:woId/dtc
 * Attach a translated DTC to a work order.
 */
async function attach(woId: number, translation: DtcTranslateResult): Promise<AttachedDtc> {
  // Backend DtcAttachBody is .strict(): { code, vehicleSnapshot?, translation? }.
  // Send the code at top level and the result nested under `translation` —
  // spreading the result's fields at top level fails with 400 unrecognized_keys.
  const res = await dtcAxios.post(`/api/work-orders/${woId}/dtc`, {
    code: translation.code,
    translation,
  });
  return res.data;
}

/**
 * GET /api/work-orders/:woId/dtc
 */
async function list(woId: number): Promise<AttachedDtc[]> {
  const res = await dtcAxios.get<{ dtcCodes: AttachedDtc[] }>(`/api/work-orders/${woId}/dtc`);
  return res.data.dtcCodes ?? (res.data as unknown as AttachedDtc[]);
}

/**
 * PATCH /api/work-orders/:woId/dtc/:id
 */
async function setStatus(woId: number, id: string, status: DtcStatus): Promise<AttachedDtc> {
  const res = await dtcAxios.patch(`/api/work-orders/${woId}/dtc/${id}`, { status });
  return res.data;
}

/**
 * DELETE /api/work-orders/:woId/dtc/:id
 */
async function remove(woId: number, id: string): Promise<void> {
  await dtcAxios.delete(`/api/work-orders/${woId}/dtc/${id}`);
}

// ─── C3 diagnosis API ────────────────────────────────────────────────────────

/**
 * GET /api/work-orders/:woId/diagnoses
 */
async function listDiagnoses(woId: number): Promise<WorkOrderDiagnosis[]> {
  const res = await dtcAxios.get<{ diagnoses: WorkOrderDiagnosis[] }>(
    `/api/work-orders/${woId}/diagnoses`
  );
  return res.data.diagnoses ?? (res.data as unknown as WorkOrderDiagnosis[]);
}

/**
 * POST /api/work-orders/:woId/diagnoses
 */
async function createDiagnosis(woId: number, body: DiagnosisBody): Promise<WorkOrderDiagnosis> {
  const res = await dtcAxios.post(`/api/work-orders/${woId}/diagnoses`, body);
  return res.data;
}

/**
 * PATCH /api/work-orders/:woId/diagnoses/:id
 */
async function updateDiagnosis(
  woId: number,
  id: string,
  body: DiagnosisBody
): Promise<WorkOrderDiagnosis> {
  const res = await dtcAxios.patch(`/api/work-orders/${woId}/diagnoses/${id}`, body);
  return res.data;
}

export const dtcService = {
  translate,
  attach,
  list,
  setStatus,
  remove,
  listDiagnoses,
  createDiagnosis,
  updateDiagnosis,
};
